import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FaExchangeAlt, FaExclamationTriangle, FaImage, FaClipboardCheck } from 'react-icons/fa';
import AnimatedModal from './AnimatedModal';
import ModalHelp from '../../../components/ModalHelp';
import BackendErrorMessage from '../../../components/BackendErrorMessage';
import api from '../../../api';

interface AbrirIntegracaoModalProps {
  banca: any;
  onClose: () => void;
  onConfirm: () => void;
}

interface EstatisticasCandidatos {
  total: number;
  comResultado: number;
  semResultado: number;
  comFoto: number;
  semFoto: number;
  totalProcesso: number;
  comFotoProcesso: number;
  semFotoProcesso: number;
}

const AbrirIntegracaoModal: React.FC<AbrirIntegracaoModalProps> = ({ 
  banca, 
  onClose, 
  onConfirm 
}) => {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<EstatisticasCandidatos | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        console.log('Buscando estatísticas para banca:', banca.id);
        const response = await api.get(`/bancas/${banca.id}/estatisticas-candidatos`);
        console.log('Estatísticas recebidas:', response.data);
        setStats(response.data);
      } catch (error) {
        console.error('Erro ao buscar estatísticas:', error);
        toast.error('Erro ao carregar estatísticas dos candidatos');
      } finally {
        setLoadingStats(false);
      }
    };
    fetchStats();
  }, [banca.id]);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
    } catch (error) {
      console.error('Erro ao abrir integração:', error);
      toast.error(BackendErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const getStatusText = (com: number, total: number) => {
    if (total === 0) return 'Nenhum candidato';
    if (com === total) return 'Todos';
    if (com === 0) return 'Nenhum';
    return 'Alguns';
  };

  return (
    <AnimatedModal isOpen={true} onClose={onClose}>
      <div style={{
        backgroundColor: 'white', padding: '32px', borderRadius: '12px',
        width: '480px', maxWidth: '90vw',
        boxShadow: '0 10px 25px rgba(0,0,0,0.15)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{ 
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: '48px', height: '48px', borderRadius: '50%',
            backgroundColor: '#dbeafe', marginBottom: '12px'
          }}>
            <FaExchangeAlt style={{ color: '#2563eb', fontSize: '20px' }} />
          </div>
          <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
            Abrir Integração
        <ModalHelp text="Abre a integração da banca para que sistemas externos possam consultar os resultados. Verifique as estatísticas de candidatos antes de confirmar." />
          </h3>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <div style={{
            backgroundColor: '#fef3c7', padding: '16px', borderRadius: '8px',
            marginBottom: '16px', border: '1px solid #fbbf24',
            display: 'flex', alignItems: 'flex-start', gap: '12px'
          }}>
            <FaExclamationTriangle style={{ color: '#d97706', fontSize: '26px', marginTop: '2px' }} />
            <div style={{ fontSize: '14px', color: '#92400e' }}>
              <strong>Atenção:</strong> Esta ação mudará o status da banca para Integração. Isso permitirá o envio de candidatos por sistema externo com permissão.
            </div>
          </div>

          <div style={{
            backgroundColor: '#fee2e2', padding: '16px', borderRadius: '8px',
            marginBottom: '16px', border: '1px solid #ef4444',
            display: 'flex', alignItems: 'flex-start', gap: '12px'
          }}>
            <FaExclamationTriangle style={{ color: '#dc2626', fontSize: '26px', marginTop: '2px' }} />
            <div style={{ fontSize: '14px', color: '#7f1d1d' }}>
              <strong>Aviso:</strong> Esta ação não pode ser desfeita.
            </div>
          </div>

          <div style={{
            backgroundColor: '#f8f9fa', padding: '16px', borderRadius: '8px',
            border: '1px solid #e9ecef'
          }}>
            <div style={{ marginBottom: '8px' }}>
              <strong>Banca:</strong> {banca.tipoBanca?.nome}
            </div>
            <div style={{ marginBottom: '8px' }}>
              <strong>Data:</strong> {banca.dataDeBanca?.data ? 
                new Date(banca.dataDeBanca.data).toLocaleDateString('pt-BR') : 'Não definida'}
            </div>
            <div>
              <strong>Status atual:</strong> {banca.status}
            </div>
          </div>

          {loadingStats ? (
            <div style={{
              backgroundColor: '#f8f9fa', padding: '16px', borderRadius: '8px',
              marginTop: '16px', textAlign: 'center', color: '#6b7280'
            }}>
              Carregando estatísticas...
            </div>
          ) : stats && (
            <div style={{
              backgroundColor: '#f0f9ff', padding: '16px', borderRadius: '8px',
              marginTop: '16px', border: '1px solid #0ea5e9'
            }}>
              <div style={{ marginBottom: '12px', fontWeight: '600', color: '#0c4a6e' }}>
                Estatísticas dos Candidatos da Banca:
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div style={{
                  backgroundColor: 'white', padding: '12px', borderRadius: '6px',
                  border: '1px solid #bae6fd'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <FaClipboardCheck style={{ color: '#0ea5e9', fontSize: '16px' }} />
                    <strong style={{ fontSize: '14px', color: '#0c4a6e' }}>Resultados</strong>
                  </div>
                  <div style={{ fontSize: '13px', color: '#475569' }}>
                    <div><strong>{getStatusText(stats.comResultado, stats.total)}</strong> têm resultado</div>
                    <div style={{ marginTop: '4px', fontSize: '12px', color: '#64748b' }}>
                      {stats.comResultado} de {stats.total} candidatos
                    </div>
                  </div>
                </div>
                <div style={{
                  backgroundColor: 'white', padding: '12px', borderRadius: '6px',
                  border: '1px solid #bae6fd'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <FaImage style={{ color: '#0ea5e9', fontSize: '16px' }} />
                    <strong style={{ fontSize: '14px', color: '#0c4a6e' }}>Fotos da Banca</strong>
                  </div>
                  <div style={{ fontSize: '13px', color: '#475569' }}>
                    <div><strong>{getStatusText(stats.comFoto, stats.total)}</strong> têm foto</div>
                    <div style={{ marginTop: '4px', fontSize: '12px', color: '#64748b' }}>
                      {stats.comFoto} de {stats.total} candidatos
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ borderTop: '1px solid #bae6fd', paddingTop: '12px' }}>
                <div style={{ marginBottom: '8px', fontWeight: '600', color: '#0c4a6e', fontSize: '13px' }}>
                  Fotos do Processo:
                </div>
                <div style={{
                  backgroundColor: 'white', padding: '12px', borderRadius: '6px',
                  border: '1px solid #bae6fd'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <FaImage style={{ color: '#0ea5e9', fontSize: '16px' }} />
                    <strong style={{ fontSize: '14px', color: '#0c4a6e' }}>Todos os Candidatos</strong>
                  </div>
                  <div style={{ fontSize: '13px', color: '#475569' }}>
                    <div><strong>{getStatusText(stats.comFotoProcesso, stats.totalProcesso)}</strong> têm foto</div>
                    <div style={{ marginTop: '4px', fontSize: '12px', color: '#64748b' }}>
                      {stats.comFotoProcesso} de {stats.totalProcesso} candidatos do processo
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px', border: '1px solid #d1d5db',
              backgroundColor: 'white', borderRadius: '6px',
              cursor: 'pointer', fontSize: '14px', fontWeight: '500'
            }}
            disabled={loading}
          >
            Cancelar
          </button>
          
          <button
            onClick={handleConfirm}
            disabled={loading}
            style={{
              padding: '10px 20px', backgroundColor: '#2563eb',
              color: 'white', border: 'none', borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px', fontWeight: '500',
              opacity: loading ? 0.6 : 1,
              display: 'flex', alignItems: 'center', gap: '6px'
            }}
          >
            {loading ? 'Abrindo...' : (
              <>
                <FaExchangeAlt style={{ fontSize: '12px' }} />
                Confirmar
              </>
            )}
          </button>
        </div>
      </div>
    </AnimatedModal>
  );
};

export default AbrirIntegracaoModal;
